import { acceptHMRUpdate, defineStore } from "pinia";
import { computed, ComputedRef, Ref, ref, watch } from "vue";
import { Event, EventDetail, EventDrilldown, EventDrilldownDetail } from "@/shared/model/event";
import { useWsStore } from "@/websocket/stores/use-ws-store";
import {
    BetCashBody,
    ClockBody,
    EventBody,
    MarketBody,
    OutcomeBody,
    PriceBody,
    ScoreBody,
} from "@/websocket/model/messages/body";
import {
    differenceBy as _differenceBy,
    differenceWith as _differenceWith,
    find as _find,
    isBoolean as _isBoolean,
    isObject as _isObject,
    maxBy as _maxBy,
    uniq as _uniq,
} from "lodash-es";
import { getEnumKeyByEnumValueFirstLetter } from "@/shared/utils/enum";
import { Status } from "@/shared/model/enums/status";
import { Outcome, OutcomeMarket } from "@/shared/model/outcome";
import { EmptyMarket, Market, MarketGroup } from "@/shared/model/market";
import { PeriodType } from "@/shared/model/enums/period-type";
import { Fact } from "@/shared/model/fact";
import { Clock } from "@/shared/model/clock";
import { State } from "@/shared/model/enums/state";
import { isDevelopmentEnv } from "@/shared/utils/environment-variables";
import { Period, PeriodPeriods } from "@/shared/model/period";
import { PubMsgQueue } from "@/websocket/model/pub-msg-queue";
import { BetData, Cashout } from "@/shared/model/bet";
import { useRouter } from "vue-router";
import { RemoveOthers } from "@/api/interfaces/common";
import { getIdsFromIdMap } from "@/shared/utils/remove-others";

// Websocket data mapping can be turned off here... For debugging purposes only!
const MAPPING_ENABLED = import.meta.env.VITE_DISABLE_WEBSOCKET_DATA_MAPPING !== "true";

// Turn off for production server and keep only for development on localhost
const extendedDebugging = isDevelopmentEnv();
// If true suffix is added to event name to signal that ws update was made (helpful when debugging)
const signalWsUpdateByEventNameSuffix = false;
// Log count of applied updates to console
const bufferDebugging = false;
// Because Chrome Developer tools has problems with excessive logging to console it is beneficial to reduce logging to only few events during debugging
const debuggingEventIds = [1000000];
const isDebuggingEventId = <T extends Event>(event: T): boolean =>
    !debuggingEventIds.length || debuggingEventIds.includes(Number(event.id));

// There are various store states (depending on event type) to which we want to save the data
export enum StateEnum {
    Bet = "bets",
    Event = "events",
    EventDetail = "eventsDetail",
    EventDrilldown = "eventsDrilldown",
    EventDrilldownDetail = "eventsDrilldownDetail",
    EventDrilldownDetailTicket = "eventsDrilldownDetailTicket",
    EventDrilldownWithBetratio = "eventsDrilldownWithBetratio",
}

interface EventsCollection {
    events?: Event[];
    eventsDetail?: EventDetail[];
    eventsDrilldown?: EventDrilldown[];
    eventsDrilldownDetail?: EventDrilldownDetail[];
    eventsDrilldownDetailTicket?: EventDrilldownDetail[];
    eventsDrilldownWithBetratio?: EventDrilldown[];
}

interface BufferExecOptions {
    executeImmediately: boolean;
    stateName?: StateEnum;
    messageId?: string;
}

// Mapping helpers
const mapStatusToStatusEnum = (status: string): Status => Status[getEnumKeyByEnumValueFirstLetter(Status, status)]; // Maps "A" to Status.Active / "S" to Status.Suspended
const mapStateToStateEnum = (state: string): State => State[getEnumKeyByEnumValueFirstLetter(State, state)]; // Maps "R" to State.Running / "S" to State.Stopped
const mapStatusToActive = (status: string): boolean => status === "A";
const mapAnswerToBoolean = (answer: string): boolean => answer === "Y";
const formatTime = (time: string): string => time.replace("+0000", "Z"); // API is using "yyyy-MM-dd'T'HH:mm:ss'Z' format, websocket returns "yyyy-MM-dd'T'HH:mm:ss+0000" (with zero based offset) so we need to convert it

const findEventById = <T extends Event>(events: T[], eventId: number): T =>
    events?.find((e: T) => e.id === String(eventId));

const findBetById = (bets: BetData[], betId: string): BetData => bets?.find((b: BetData) => b.id === betId);

const updateEventsFromApi = <T extends Event>(
    curEvents: T[],
    apiEvents: T[],
    removeOthers: RemoveOthers | boolean | undefined,
): void => {
    // Keep in mind that we need to directly mutate arrays here without any reassignment to keep reactivity functional... so bye bye spread operator
    if (!curEvents || !apiEvents) return;
    // When mapping is disabled we need to do a full update instead of partial
    if (!MAPPING_ENABLED) {
        for (const apiEvent of apiEvents) {
            const idx = curEvents.map((e) => e.id).indexOf(apiEvent.id);
            if (idx > -1) {
                curEvents[idx] = apiEvent;
            } else {
                curEvents.push(apiEvent);
            }
        }
        return;
    }
    // Remove other events which were stored previously
    if (removeOthers !== undefined) {
        if (_isBoolean(removeOthers) && removeOthers) {
            curEvents.length = 0;
        } else if (_isObject(removeOthers) && removeOthers[Object.keys(removeOthers)[0]]?.eventIds) {
            const removeOtherIds = getIdsFromIdMap(removeOthers, "eventIds");
            for (let i = curEvents.length - 1; i >= 0; i--) {
                if (!removeOtherIds.includes(curEvents[i].id)) {
                    curEvents.splice(i, 1);
                }
            }
        } else if (_isObject(removeOthers) && removeOthers[Object.keys(removeOthers)[0]]?.leagueIds) {
            const removeOtherIds = getIdsFromIdMap(removeOthers, "leagueIds");
            for (let i = curEvents.length - 1; i >= 0; i--) {
                if (!removeOtherIds.includes(curEvents[i].competitionDrilldownTagId)) {
                    curEvents.splice(i, 1);
                }
            }
        }
    }
    // Add new events from api
    const newEvents = _differenceBy(apiEvents, curEvents, "id");
    if (!curEvents) curEvents = []; // Just a failsafe
    newEvents.forEach((ne) => {
        curEvents.push(ne);
    });
    // Remove resulted events
    const resultedEventIds = curEvents.filter((e) => !e.active && e.resulted)?.map((e) => e.id);
    if (resultedEventIds?.length > 0) {
        for (let i = curEvents.length - 1; i >= 0; i--) {
            if (resultedEventIds.includes(curEvents[i].id)) {
                curEvents.splice(i, 1);
            }
        }
    }
    // Flat maps are here to improve performance (reduce nested looping)
    const apiMarkets = apiEvents.flatMap((e) => e?.markets || []);
    const apiGroupedMarkets = apiEvents.flatMap((e) => e?.groupedMarkets || []);
    const apiOutcomes = apiMarkets.flatMap((m) => m?.outcomes || []);
    const apiGroupedOutcomes = apiGroupedMarkets.map((m) => m?.outcomes || []).flat();

    // We'll do partial update here (only add new entities like outcomes, markets, periods and remove old ones)... the rest is kept untouched and will be updated via websocket
    curEvents
        .filter((ce) => apiEvents.map((e) => e.id).includes(ce.id))
        .forEach((event) => {
            const apiEventsEvent = apiEvents.find((ae) => ae.id === event.id);
            // Only EventDetail and EventDrilldownDetail contains "collections" array
            // Unfortunately we need to keep it up to date because calculations on detail pages are based on it
            if (event?.["collections"] && apiEventsEvent?.["collections"]) {
                if (extendedDebugging && isDebuggingEventId(event))
                    console.log(
                        "~event - API -",
                        "event.collections",
                        event?.["collections"],
                        "apiEventsEvent.commentary",
                        apiEventsEvent?.["collections"],
                    );
                event["collections"] = apiEventsEvent?.["collections"];
            }
            // Sometimes happens that endpoint returns commentary as null... the bad thing is when it is on initial call, so we need to wait on next refresh until data arrives
            if (!event?.commentary && apiEventsEvent?.commentary) {
                if (extendedDebugging && isDebuggingEventId(event))
                    console.log(
                        "~event - API -",
                        "event.commentary",
                        event.commentary,
                        "apiEventsEvent.commentary",
                        apiEventsEvent.commentary,
                    );
                event.commentary = apiEventsEvent.commentary;
            }
            // Update periods.periods
            event.commentary?.periods?.forEach((p) => {
                const apiEventsPeriodPeriods = apiEventsEvent?.commentary?.periods?.find(
                    (pp) => pp.periodIndex === p.periodIndex && pp.type === p.type,
                )?.periods;
                const newPeriodPeriods = _differenceWith(
                    apiEventsPeriodPeriods,
                    p?.periods,
                    (a, b) => a.periodIndex === b.periodIndex && a.type === b.type,
                );
                if (extendedDebugging && isDebuggingEventId(event) && newPeriodPeriods.length)
                    console.log(
                        "~event - API -",
                        "newPeriodPeriods",
                        newPeriodPeriods,
                        "currentPeriodPeriods",
                        p?.periods,
                    );
                if (!p?.periods && newPeriodPeriods?.length > 0) p.periods = []; // Crappy api sometimes does not return periods correctly and returns null instead
                // Add new periods
                newPeriodPeriods.forEach((npp) => {
                    p.periods.push(npp);
                });
                const apiPeriodPeriodsIdentification = apiEventsPeriodPeriods?.map((aepp) => ({
                    periodIndex: aepp.periodIndex,
                    type: aepp.type,
                }));
                // Remove periods which were not returned by api
                if (apiPeriodPeriodsIdentification?.length > 0) {
                    for (let i = p.periods.length; i--; ) {
                        if (
                            !_find(apiPeriodPeriodsIdentification, {
                                periodIndex: p.periods[i].periodIndex,
                                type: p.periods[i].type,
                            })
                        ) {
                            p.periods.splice(i, 1);
                        }
                    }
                }
            });
            // Update periods
            const newPeriods = _differenceWith(
                apiEventsEvent?.commentary?.periods,
                event.commentary?.periods,
                (a, b) => a.periodIndex === b.periodIndex && a.type === b.type,
            );
            if (extendedDebugging && isDebuggingEventId(event) && newPeriods.length)
                console.log("~event - API -", "newPeriods", newPeriods, "currentPeriods", event.commentary?.periods);
            if (!event.commentary?.periods && newPeriods?.length > 0) event.commentary.periods = []; // Crappy api sometimes does not return periods correctly and returns null instead
            // Add new periods
            newPeriods.forEach((p) => {
                event.commentary.periods.push(p);
            });
            const apiPeriodsIdentification = apiEventsEvent?.commentary?.periods?.map((aecp) => ({
                periodIndex: aecp.periodIndex,
                type: aecp.type,
            }));
            // Remove periods which were not returned by api
            if (apiPeriodsIdentification?.length > 0) {
                for (let i = event.commentary.periods.length; i--; ) {
                    if (
                        !_find(apiPeriodsIdentification, {
                            periodIndex: event.commentary.periods[i].periodIndex,
                            type: event.commentary.periods[i].type,
                        })
                    ) {
                        event.commentary.periods.splice(i, 1);
                    }
                }
            }
            // BEWARE: currentActiveOffset is not OB value and is created by middleware hence there are no updates via websocket, but we need to keep it up to date
            // currentActiveOffset appears only in events witch code 'football', because updateEventsFromApi is universal function using generics
            // we cannot get sport code here for Event object, and we need to check if period contains currentActiveOffset instead
            const apiEventsEventPeriodsWithCurrentActiveOffset =
                apiEventsEvent.commentary?.periods?.filter((p) => p.currentActiveOffset !== undefined) || [];
            if (apiEventsEventPeriodsWithCurrentActiveOffset.length) {
                event.commentary.periods.forEach((p) => {
                    const newCurrentActiveOffset = apiEventsEventPeriodsWithCurrentActiveOffset.find(
                        (ap) => p.periodIndex === ap.periodIndex && p.type === ap.type,
                    )?.currentActiveOffset;
                    if (newCurrentActiveOffset !== undefined) {
                        p.currentActiveOffset = newCurrentActiveOffset;
                    }
                });
            }
            // BEWARE: We are mixing here data from two different data sets (non-grouped/grouped markets), can have side effects
            // Update markets.outcomes
            const isNonGroupedMarketsApiCall = apiEventsEvent.markets !== undefined;
            if (isNonGroupedMarketsApiCall) {
                event?.markets?.forEach((m) => {
                    const apiOutcomesMarket = apiOutcomes.filter((ao) => ao.marketId === m.id);
                    const newOutcomes = _differenceBy(apiOutcomesMarket, m.outcomes, "id");
                    if (extendedDebugging && isDebuggingEventId(event) && newOutcomes.length)
                        console.log("~event - API -", "newOutcomes", newOutcomes, "currentOutcomes", m?.outcomes);
                    // Add new outcomes
                    newOutcomes.forEach((o) => {
                        m.outcomes.push(o);
                    });
                    // Remove outcomes which were not returned by api
                    for (let i = m.outcomes.length; i--; ) {
                        if (!apiOutcomesMarket.map((aom) => aom.id).includes(m.outcomes[i].id)) {
                            m.outcomes.splice(i, 1);
                        }
                    }
                });
            }
            // Update groupedMarkets.outcomes
            const isGroupedMarketsApiCall = apiEventsEvent.groupedMarkets !== undefined;
            if (isGroupedMarketsApiCall) {
                event?.groupedMarkets?.forEach((m) => {
                    const apiGroupedOutcomesMarket = apiGroupedOutcomes.filter(
                        (ao) => ao.marketId === m.id || (m as MarketGroup)?.marketIds.includes(ao.marketId),
                    );
                    const newGroupedOutcomes = _differenceBy(
                        apiGroupedOutcomesMarket,
                        m.outcomes as (OutcomeMarket | Outcome | EmptyMarket)[],
                        "id",
                    );
                    if (extendedDebugging && isDebuggingEventId(event) && newGroupedOutcomes.length)
                        console.log(
                            "~event - API -",
                            "newGroupedOutcomes",
                            newGroupedOutcomes,
                            "currentGroupedOutcomes",
                            m?.outcomes,
                        );
                    // Add new outcomes
                    newGroupedOutcomes.forEach((o) => {
                        (m.outcomes as (OutcomeMarket | Outcome | EmptyMarket)[]).push(o);
                    });
                    // Remove outcomes which were not returned by api
                    for (let i = m.outcomes.length; i--; ) {
                        if (!apiGroupedOutcomesMarket.map((aom) => aom.id).includes(m.outcomes[i].id)) {
                            m.outcomes.splice(i, 1);
                        }
                    }
                });
            }
            // Update markets
            if (event?.markets) {
                const apiMarketsEvent = apiMarkets.filter((am) => am.eventId === event.id);
                const newMarkets = _differenceBy(apiMarketsEvent, event.markets, "id");
                if (extendedDebugging && isDebuggingEventId(event) && newMarkets.length)
                    console.log("~event - API -", "newMarkets", newMarkets, "currentMarkets", event?.markets);
                // Add new markets
                newMarkets.forEach((m) => {
                    event.markets.push(m);
                });
                // Remove markets which were not returned by api
                if (isNonGroupedMarketsApiCall) {
                    for (let i = event.markets.length; i--; ) {
                        if (!apiMarketsEvent.map((ame) => ame.id).includes(event.markets[i].id)) {
                            event.markets.splice(i, 1);
                        }
                    }
                }
            } else if (isNonGroupedMarketsApiCall) {
                event.markets = apiEventsEvent.markets;
            }
            // Update groupedMarkets
            if (event?.groupedMarkets) {
                const apiGroupedMarketsEvent = apiGroupedMarkets.filter((am) => am.eventId === event.id);
                const newGroupedMarkets = _differenceWith(
                    apiGroupedMarketsEvent,
                    event.groupedMarkets,
                    (a, b) => a.groupCode === b.groupCode && a.marketName === b.marketName,
                );
                if (extendedDebugging && isDebuggingEventId(event) && newGroupedMarkets.length)
                    console.log(
                        "~event - API -",
                        "newGroupedMarkets",
                        newGroupedMarkets,
                        "currentGroupedMarkets",
                        event?.groupedMarkets,
                    );
                // Add new markets
                newGroupedMarkets.forEach((m) => {
                    event.groupedMarkets.push(m);
                });
                // Remove groupedMarkets which were not returned by api
                if (isGroupedMarketsApiCall) {
                    for (let i = event.groupedMarkets.length; i--; ) {
                        if (
                            !apiGroupedMarketsEvent.some(
                                (ame) =>
                                    ame.groupCode === event.groupedMarkets[i].groupCode &&
                                    ame.marketName === event.groupedMarkets[i].marketName,
                            )
                        ) {
                            event.groupedMarkets.splice(i, 1);
                        }
                    }
                }
            } else if (isGroupedMarketsApiCall) {
                event.groupedMarkets = apiEventsEvent.groupedMarkets;
            }
            // Update marketCounts which should correlate with api data
            if (event?.marketCounts) {
                event.marketCounts = apiEventsEvent?.marketCounts;
            }
            // Heh... this is nice corner case
            // When we load events as prematch there is no information sent via websocket that it become live (can 'started' be used for setting 'liveNow'?)
            // We need to relly on api data here
            if (!event.liveNow && apiEventsEvent?.liveNow) {
                event.liveNow = apiEventsEvent.liveNow;
            }
        });
};

const updateBetsFromApi = (curBets: BetData[], apiBets: BetData[]): void => {
    // Keep in mind that we need to directly mutate arrays here without any reassignment to keep reactivity functional... so bye bye spread operator
    if (!curBets || !apiBets) return;
    // We need to do a full update instead of partial because every call to /bets ep can return different data based on query params and bet actual state
    for (const apiBet of apiBets) {
        const idx = curBets.map((e) => e.id).indexOf(apiBet.id);
        if (idx > -1) {
            curBets[idx] = apiBet;
        } else {
            curBets.push(apiBet);
        }
    }
};

const updateClockFromBody = (event: Event, body: ClockBody) => {
    if (!event) return;
    if (extendedDebugging && signalWsUpdateByEventNameSuffix) {
        event.name = `${event.name} EC`;
    }
    // Ignore non-existent periods... needs to be fetched from API first
    if (body.period_code === PeriodType.All) {
        event.commentary?.periods?.forEach((period) => {
            period.clock.offset = body.offset;
            period.clock.lastUpdate = formatTime(body.last_update);
            period.clock.state = mapStateToStateEnum(body.state);
        });
        if (extendedDebugging && isDebuggingEventId(event))
            !event.commentary?.periods?.length
                ? console.log("~clock - ALL -", "body", body, "clocks", `periods array is ${event.commentary.periods}`)
                : console.log(
                      "~clock - ALL -",
                      "body",
                      body,
                      "clocks",
                      event.commentary.periods.map((p) => p.clock),
                  );
    } else {
        let clock: Clock = event.commentary?.periods?.find(
            (p) => p.periodIndex === body.period_index && p.type === body.period_code,
        )?.clock;
        if (!clock && event.commentary?.periods?.length > 0) {
            // Search for clock in all periods include its children (we take here parent with highest periodIndex)
            const periods: Period[] = _maxBy(event.commentary.periods, "periodIndex")?.periods;
            clock = periods?.find((p) => p.periodIndex === body.period_index && p.type === body.period_code)?.clock;
        }
        if (clock) {
            clock.offset = body.offset;
            clock.lastUpdate = formatTime(body.last_update);
            clock.state = mapStateToStateEnum(body.state);
        }
        if (extendedDebugging && isDebuggingEventId(event))
            console.log(`~clock - ${!clock ? "NO" : "YES"} -`, "body", body, "clock", clock);
    }
};

const updateEventFromBody = (event: Event, body: EventBody) => {
    if (!event) return;
    if (extendedDebugging && signalWsUpdateByEventNameSuffix) {
        event.name = `${event.name} E`;
    }
    event.status = mapStatusToStatusEnum(body.status);
    event.active = mapStatusToActive(body.status);
    event.startTime = formatTime(body.start_time);
    event.displayed = mapAnswerToBoolean(body.displayed);
    event.started = mapAnswerToBoolean(body.started);
    event.displayOrder = body.disporder;
    event.resulted = mapAnswerToBoolean(body.result_conf);
    // Will never happen that event is undefined here but this is expected (not all store states contains all events)... we don't want to overpopulate the log with useless data
    if (extendedDebugging && isDebuggingEventId(event))
        console.log(`~event - ${!event ? "NO" : "YES"} -`, "body", body, "event", event);
};

const updateMarketFromBody = (event: Event, body: MarketBody, marketId: number) => {
    if (!event) return;
    const market = event?.markets?.find((m: Market) => Number(m.id) === marketId);
    if (market) {
        if (extendedDebugging && signalWsUpdateByEventNameSuffix) {
            event.name = `${event.name} EM`;
        }
        const activeOld = market.active;
        const activeNew = mapStatusToActive(body.status);
        market.type = body.mkt_type;
        market.subType = body.mkt_sort;
        market.eventId = body.ev_id.toString();
        market.status = mapStatusToStatusEnum(body.status);
        market.active = activeNew;
        market.displayed = mapAnswerToBoolean(body.displayed);
        market.displayOrder = body.disporder;
        market.betInRun = mapAnswerToBoolean(body.bet_in_run);
        if (body.bir_index) market.betInRunIndex = Number(body.bir_index);
        // markets.handcapValue is not updatable via websocket, so body.hcap_values is ignored here... in case that markets.handcapValue needs to be updated current market is dismissed and replaced by new one via api
        // If 'active' changes update all market's outcomes as well to the same value
        if (activeOld !== activeNew) {
            market.outcomes.forEach((o) => (o.active = activeNew));
        }
    }
    // This is a bit tricky... grouped markets does not contain all attributes as regular markets, and we can only update underlying outcomes
    // BEWARE: This is possible place for issues if some of the omitted attributes will be needed in the future
    const groupedMarkets = event?.groupedMarkets?.filter(
        (m) => Number(m.id) === marketId || (m as MarketGroup)?.marketIds?.includes(marketId.toString()),
    );
    if (groupedMarkets?.length > 0) {
        const activeNew = mapStatusToActive(body.status);
        const statusNew = mapStatusToStatusEnum(body.status);
        groupedMarkets.forEach((m) => {
            // GROUP TAKES LOWEST displayOrder VALUE FROM MARKETS IN GROUP
            m.displayOrder = m.displayOrder > body.disporder ? body.disporder : m.displayOrder;
            m?.outcomes
                .filter((o) => o.marketId === marketId.toString())
                .forEach((o) => {
                    o.active = activeNew;
                    if ((o as unknown as OutcomeMarket)?.parentMarket) {
                        (o as unknown as OutcomeMarket).parentMarket.active = activeNew;
                        (o as unknown as OutcomeMarket).parentMarket.status = statusNew;
                        (o as unknown as OutcomeMarket).parentMarket.eventId = body.ev_id.toString();
                    }
                });
        });
    }
    if (extendedDebugging && isDebuggingEventId(event)) {
        console.log(`~market - ${!market ? "NO" : "YES"} -`, "body", body, "market", market);
        console.log(
            `~groupedMarkets - ${!groupedMarkets?.length ? "NO" : "YES"} -`,
            "body",
            body,
            "groupedMarkets",
            groupedMarkets,
        );
    }
};

const updateOutcomeFromBody = (event: Event, body: OutcomeBody, outcomeId: number) => {
    if (!event) return;
    const outcome = (event?.groupedMarkets || event?.markets)
        ?.flatMap((m: Market) => m.outcomes)
        ?.find((o: Outcome) => Number(o.id) === outcomeId);
    if (outcome) {
        if (extendedDebugging && signalWsUpdateByEventNameSuffix) {
            event.name = `${event.name} EO`;
        }
        outcome.marketId = body.ev_mkt_id.toString();
        outcome.status = mapStatusToStatusEnum(body.status);
        outcome.active = mapStatusToActive(body.status);
        outcome.displayed = mapAnswerToBoolean(body.displayed);
        outcome.displayOrder = body.disporder;
        updatePriceFromBody(event, body as unknown as PriceBody, outcomeId);
    }
    if (extendedDebugging && isDebuggingEventId(event))
        console.log(`~outcome - ${!outcome ? "NO" : "YES"} -`, "body", body, "outcome", outcome);
};

const updatePriceFromBody = (event: Event, body: PriceBody, outcomeId: number) => {
    if (!event) return;
    const outcome = (event?.groupedMarkets || event?.markets)
        ?.flatMap((m: Market) => m.outcomes)
        ?.find((o: Outcome) => Number(o.id) === outcomeId);
    if (outcome?.prices[0]) {
        if (extendedDebugging && signalWsUpdateByEventNameSuffix) {
            event.name = `${event.name} EP`;
        }
        const newPrice = { ...outcome?.prices[0] };
        newPrice.numerator = Number(body.lp_num);
        newPrice.denominator = Number(body.lp_den);
        newPrice.decimal = Number((Number(body.lp_num) / Number(body.lp_den) + 1).toFixed(2)); // Conversion to number removes trailing zero(s)... is it behavior what we want to achieve?
        outcome.prices[0] = newPrice;
    }
    if (extendedDebugging && isDebuggingEventId(event))
        console.log(`~price - ${!outcome?.prices[0] ? "NO" : "YES"} -`, "body", body, "price", outcome?.prices[0]);
};

const updateScoreFromBody = (event: Event, body: ScoreBody) => {
    if (!event) return;
    if (extendedDebugging && signalWsUpdateByEventNameSuffix) {
        event.name = `${event.name} ES`;
    }
    // Ignore non-existent facts... needs to be fetched from API first
    // Unfortunately creates a small discrepancy in tennis when game (part of the set) is over and we need to wait for a new set from api
    let fact: Fact = event.commentary?.facts?.find(
        (f) => f.participantId === body.participant_id && f.type === body.code,
    );
    if (body.period_code !== PeriodType.All) {
        // Search for fact in all periods include its children
        // const facts: Fact[] = findPeriod(event, body.period_code, body.period_index)?.facts;
        let period: PeriodPeriods | Period = event.commentary?.periods?.find(
            (p) => p.periodIndex === body.period_index && p.type === body.period_code,
        );
        if (!period && event.commentary?.periods?.length > 0) {
            period = _maxBy(event.commentary.periods, "periodIndex")?.periods.find(
                (p) => p.periodIndex === body.period_index && p.type === body.period_code,
            );
        }
        fact = period?.facts?.find((f) => f.participantId === body.participant_id && f.type === body.code);
    }

    if (fact) {
        fact.value = body.value;
    }
    if (extendedDebugging && isDebuggingEventId(event))
        console.log(`~score - ${!fact ? "NO" : "YES"} -`, "body", body, "fact", fact);
};

const updateBetCashFromBody = (bet: BetData, body: BetCashBody) => {
    if (!bet) return;
    bet.channel = body?.betChannel || bet.channel;
    if (body.cashoutAvailable) {
        bet.cashout = {
            status: "A",
            value: body?.amount,
            available: body.cashoutAvailable,
            betPlacedTime: body?.betPlacedTime,
            cashoutCalculationTime: body?.cashoutTime,
            customerId: body?.customerId,
        } as Cashout;
    } else {
        bet.cashout = undefined;
    }
};

const DEFAULT_BUFFER_FLUSH_INTERVAL = 1000;
// Do NOT use directly... use 'Api' store instead
const useApiPrivate = () => {
    const wsStore = useWsStore();
    const bufferFlushInterval: Ref<number> = ref(DEFAULT_BUFFER_FLUSH_INTERVAL);
    const bufferFlushTimestamp: Ref<number> = ref(0);
    const events: Ref<Event[]> = ref([]);
    const eventsDetail: Ref<EventDetail[]> = ref([]);
    const eventsDrilldown: Ref<EventDrilldown[]> = ref([]);
    const eventsDrilldownDetail: Ref<EventDrilldownDetail[]> = ref([]);
    const eventsDrilldownDetailTicket: Ref<EventDrilldownDetail[]> = ref([]);
    const eventsDrilldownWithBetratio: Ref<EventDrilldown[]> = ref([]);
    const bets: Ref<BetData[]> = ref([]);
    const eventIds: ComputedRef<number[]> = computed(() =>
        _uniq([
            ...(events.value?.length ? events.value.map((e) => Number(e.id)) : []),
            ...(eventsDetail.value?.map((e) => Number(e.id)) || []),
            ...(eventsDrilldown.value?.length ? eventsDrilldown.value.map((e) => Number(e.id)) : []),
            ...(eventsDrilldownDetail.value?.map((e) => Number(e.id)) || []),
            ...(eventsDrilldownDetailTicket.value?.map((e) => Number(e.id)) || []),
            ...(eventsDrilldownWithBetratio.value?.map((e) => Number(e.id)) || []),
        ]).sort((a: number, b: number) => a - b),
    );
    const setBufferFlushInterval = (interval: number) => {
        bufferFlushInterval.value = interval;
    };
    const getStateRefByName = (stateName: StateEnum) => {
        switch (stateName) {
            case StateEnum.Bet:
                return bets;
            case StateEnum.Event:
                return events;
            case StateEnum.EventDetail:
                return eventsDetail;
            case StateEnum.EventDrilldown:
                return eventsDrilldown;
            case StateEnum.EventDrilldownDetail:
                return eventsDrilldownDetail;
            case StateEnum.EventDrilldownDetailTicket:
                return eventsDrilldownDetailTicket;
            case StateEnum.EventDrilldownWithBetratio:
                return eventsDrilldownWithBetratio;
        }
    };
    const reset = (all: boolean = true) => {
        if (all) {
            $reset();
        } else {
            bufferFlushInterval.value = DEFAULT_BUFFER_FLUSH_INTERVAL;
            bufferFlushTimestamp.value = 0;
            bets.value = [];
            events.value = [];
            eventsDetail.value = [];
            eventsDrilldown.value = [];
            eventsDrilldownDetail.value = [];
            // Do not reset eventsDrilldownDetailTicket here... It needs to stay untouched
            eventsDrilldownWithBetratio.value = [];
        }
    };
    const $reset = () => {
        bufferFlushInterval.value = DEFAULT_BUFFER_FLUSH_INTERVAL;
        bufferFlushTimestamp.value = 0;
        bets.value = [];
        events.value = [];
        eventsDetail.value = [];
        eventsDrilldown.value = [];
        eventsDrilldownDetail.value = [];
        eventsDrilldownDetailTicket.value = [];
        eventsDrilldownWithBetratio.value = [];
    };
    // additional logic
    // We group state updates in queue and apply whole queue in regular cycles to reduce performance overhead
    const buffer: PubMsgQueue = new PubMsgQueue();
    let bufferFlushIntervalId = null;
    const addToBuffer = (fc: (state) => void, options?: BufferExecOptions) => {
        if (options?.executeImmediately) {
            if (options?.stateName) {
                fc(getStateRefByName(options.stateName)?.value);
            } else {
                for (const key in StateEnum) {
                    if (StateEnum[key] === StateEnum.Bet) continue;
                    fc(getStateRefByName(StateEnum[key])?.value);
                }
            }
            bufferFlushTimestamp.value = performance.now();
        } else {
            buffer.add({ fc, stateName: options?.stateName, messageId: options?.messageId });
        }
    };
    const applyBufferedUpdates = () => {
        // We need to maintain correct order of applied messages (by their messageIds) because sometimes happen that messages with contradictory data arrive at the same millisecond
        buffer.sort();
        const updatesCount = buffer.length();
        for (let item = buffer.read(); item !== undefined; item = buffer.read()) {
            if (!item?.stateName) {
                for (const key in StateEnum) {
                    if (StateEnum[key] === StateEnum.Bet || getStateRefByName(StateEnum[key])?.value?.length === 0)
                        continue;
                    item.fc(getStateRefByName(StateEnum[key])?.value);
                }
            } else {
                item.fc(getStateRefByName(item.stateName)?.value);
            }
        }
        if (updatesCount) bufferFlushTimestamp.value = performance.now();
        if (extendedDebugging && bufferDebugging) console.log("Buffer updates applied:", updatesCount);
    };
    watch(
        bufferFlushInterval,
        (newValue) => {
            if (newValue) {
                applyBufferedUpdates();
                bufferFlushIntervalId = setInterval(() => {
                    applyBufferedUpdates();
                }, bufferFlushInterval.value);
            } else if (bufferFlushIntervalId) {
                clearInterval(bufferFlushIntervalId);
            }
        },
        { immediate: true },
    );
    watch(eventIds, (newValue) => {
        // TODO: Timeout is here just to flatten CPU spikes... Can be removed in the future once problem with excessive amount of ws messages after subscription will be solved
        setTimeout(() => wsStore.setSubscribedChannelsByEventIds(newValue), 1000);
    });
    return {
        bufferFlushInterval,
        bufferFlushTimestamp,
        events,
        eventsDetail,
        eventsDrilldown,
        eventsDrilldownDetail,
        eventsDrilldownDetailTicket,
        eventsDrilldownWithBetratio,
        bets,
        eventIds,
        setBufferFlushInterval,
        addToBuffer,
        getStateRefByName,
        reset,
        $reset,
    };
};

// Seems a bit overcomplicated for the first sight (and it is), but it has its reason... performance!
// Direct mutation of stored objects in Pinia store is costly, especially in large object with huge number of nested objects and when objects needs to be updated in high frequency (by websocket data).
// Because of this we need to use 'ApiPrivate' which is plain composable (not Pinia store) where all updates takes place in defined update loops.
// At the same time in 'Api' store we check 'ApiPrivate' buffer timestamp to apply multiple changes at the same time and avoid unnecessary watcher triggering.
// Note: There are plenty of problems in Vue DevTools in Pinia module (even after more than year from now what we use Pinia it's not mature enough) and in Pinia itself when stores are updated in high frequency (problems in timeline).
export const useApiStore = defineStore("Api", () => {
    const wsStore = useWsStore();
    const apiPrivate = useApiPrivate();

    // state
    const eventsCollection: ComputedRef<EventsCollection> = computed(() =>
        // Change of timestamp will trigger re-calculation of the computed property (do not use ComputedRefWithControl... it does not work well in Pinia)
        ({
            events: apiPrivate.bufferFlushTimestamp.value ? apiPrivate.events.value : [],
            eventsDetail: apiPrivate.bufferFlushTimestamp.value ? apiPrivate.eventsDetail.value : [],
            eventsDrilldown: apiPrivate.bufferFlushTimestamp.value ? apiPrivate.eventsDrilldown.value : [],
            eventsDrilldownDetail: apiPrivate.bufferFlushTimestamp.value ? apiPrivate.eventsDrilldownDetail.value : [],
            eventsDrilldownDetailTicket: apiPrivate.bufferFlushTimestamp.value
                ? apiPrivate.eventsDrilldownDetailTicket.value
                : [],
            eventsDrilldownWithBetratio: apiPrivate.bufferFlushTimestamp
                ? apiPrivate.eventsDrilldownWithBetratio.value
                : [],
        }),
    );
    const bets: ComputedRef<BetData[]> = computed(() =>
        apiPrivate.bufferFlushTimestamp.value ? apiPrivate.bets.value : [],
    );
    const eventIds: ComputedRef<number[]> = computed(() =>
        apiPrivate.bufferFlushTimestamp.value ? apiPrivate.eventIds.value : [],
    );

    // actions
    const getEventsFromEventsCollectionByIds = <T extends Event>(stateName: StateEnum, ids: string[]): T[] => {
        return eventsCollection.value[stateName].filter((e) => ids.includes(e.id)) as T[];
    };
    const getBetsFromBetsByIds = (ids: string[]): BetData[] => {
        return bets.value.filter((b) => ids.includes(b.id));
    };
    const storeEvents = async <T extends Event>(
        stateName: StateEnum,
        evs: T[] | undefined,
        removeOthers: RemoveOthers | boolean | undefined,
    ): Promise<T[] | undefined> => {
        if (evs === undefined || !evs || stateName === StateEnum.Bet) return;
        apiPrivate.addToBuffer(
            (state) => {
                updateEventsFromApi(state, evs, removeOthers);
            },
            {
                executeImmediately: true,
                stateName,
            },
        );
        return getEventsFromEventsCollectionByIds(
            stateName,
            evs.map((e: T) => e.id),
        );
    };
    const storeBets = async (bts: BetData[] | undefined): Promise<BetData[] | undefined> => {
        if (bts === undefined) return;
        apiPrivate.addToBuffer(
            (state) => {
                updateBetsFromApi(state, bts);
            },
            {
                executeImmediately: true,
                stateName: StateEnum.Bet,
            },
        );
        return getBetsFromBetsByIds(bts.map((b: BetData) => b.id));
    };

    const updateEvent = (messageId: string, eventId: number, body: EventBody) => {
        apiPrivate.addToBuffer(
            (state) => {
                updateEventFromBody(findEventById(state, eventId), body);
                if (extendedDebugging && bufferDebugging) console.log("event");
            },
            { executeImmediately: false, messageId },
        );
    };
    const updateEventClock = (messageId: string, eventId: number, body: ClockBody) => {
        apiPrivate.addToBuffer(
            (state) => {
                updateClockFromBody(findEventById(state, eventId), body);
                if (extendedDebugging && bufferDebugging) console.log("clock");
            },
            { executeImmediately: false, messageId },
        );
    };
    const updateEventMarket = (messageId: string, eventId: number, marketId: number, body: MarketBody) => {
        apiPrivate.addToBuffer(
            (state) => {
                updateMarketFromBody(findEventById(state, eventId), body, marketId);
                if (extendedDebugging && bufferDebugging) console.log("market");
            },
            { executeImmediately: false, messageId },
        );
    };
    const updateEventOutcome = (messageId: string, eventId: number, outcomeId: number, body: OutcomeBody) => {
        apiPrivate.addToBuffer(
            (state) => {
                updateOutcomeFromBody(findEventById(state, eventId), body, outcomeId);
                if (extendedDebugging && bufferDebugging) console.log("outcome");
            },
            { executeImmediately: false, messageId },
        );
    };
    const updateEventPrice = (messageId: string, eventId: number, outcomeId: number, body: PriceBody) => {
        apiPrivate.addToBuffer(
            (state) => {
                updatePriceFromBody(findEventById(state, eventId), body, outcomeId);
                if (extendedDebugging && bufferDebugging) console.log("price");
            },
            { executeImmediately: false, messageId },
        );
    };
    const updateEventScore = (messageId: string, eventId: number, body: ScoreBody) => {
        apiPrivate.addToBuffer(
            (state) => {
                updateScoreFromBody(findEventById(state, eventId), body);
                if (extendedDebugging && bufferDebugging) console.log("score");
            },
            { executeImmediately: false, messageId },
        );
    };
    const updateBetCash = (messageId: string, body: BetCashBody) => {
        apiPrivate.addToBuffer(
            (state) => {
                updateBetCashFromBody(findBetById(state, body.betId), body);
                if (extendedDebugging && bufferDebugging) console.log("betcash");
            },
            { executeImmediately: false, stateName: StateEnum.Bet, messageId },
        );
    };
    const reset = async (all: boolean = true) => {
        await wsStore.reset(all);
        apiPrivate.reset(all);
    };
    const $reset = () => {
        apiPrivate.$reset();
    };

    // additional logic
    const router = useRouter();
    router.afterEach(async () => {
        await reset(false);
    });

    return {
        bets,
        eventsCollection,
        eventIds,
        getEventsFromEventsCollectionByIds,
        storeEvents,
        storeBets,
        updateEvent,
        updateEventClock,
        updateEventMarket,
        updateEventOutcome,
        updateEventPrice,
        updateEventScore,
        updateBetCash,
        reset,
        $reset,
    };
});

if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(useApiStore, import.meta.hot));
}
